import { Calendar, ChevronsUpDown, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangeFilterProps {
  value: { days?: number; from?: string; to?: string } | undefined;
  onChange: (value: { days?: number; from?: string; to?: string } | undefined) => void;
  placeholder?: string;
  className?: string;
  /** Show custom date range picker (default: true) */
  showCustomRange?: boolean;
  disabled?: boolean;
}

const PRESET_OPTIONS = [
  { label: 'Last 24 hours', days: 1 },
  { label: 'Last 3 days', days: 3 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 60 days', days: 60 },
  { label: 'Last 90 days', days: 90 },
];

/**
 * Format a date string (YYYY-MM-DD) to a more readable format (MMM D)
 */
function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function DateRangeFilter({
  value,
  onChange,
  placeholder = 'date: All',
  className,
  showCustomRange = true,
  disabled = false,
}: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState(value?.from || '');
  const [customTo, setCustomTo] = useState(value?.to || '');

  const getDisplayLabel = () => {
    if (!value) return placeholder;
    if (value.days) {
      if (value.days === 1) return 'Last 24 hours';
      return `Last ${value.days} days`;
    }
    if (value.from && value.to) {
      return `${formatDateShort(value.from)} → ${formatDateShort(value.to)}`;
    }
    if (value.from) {
      return `From ${formatDateShort(value.from)}`;
    }
    if (value.to) {
      return `Until ${formatDateShort(value.to)}`;
    }
    return placeholder;
  };

  const hasValue = value?.days || value?.from || value?.to;

  const handleApplyCustom = () => {
    if (customFrom || customTo) {
      onChange({ from: customFrom || undefined, to: customTo || undefined });
    }
    setShowCustom(false);
    setOpen(false);
  };

  const handleClearCustom = () => {
    setCustomFrom('');
    setCustomTo('');
  };

  // Reset custom inputs when popover opens
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setCustomFrom(value?.from || '');
      setCustomTo(value?.to || '');
      setShowCustom(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('h-9 w-full justify-between text-sm font-normal', className)}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span
              className={cn('truncate', hasValue ? 'text-foreground' : 'text-muted-foreground')}
            >
              {getDisplayLabel()}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {hasValue && !disabled && (
              <X
                className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(undefined);
                }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-2" align="start">
        {!showCustom ? (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground px-2 py-1">Quick select</p>
            {PRESET_OPTIONS.map((option) => (
              <button
                key={option.days}
                onClick={() => {
                  onChange({ days: option.days });
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-accent transition-colors',
                  value?.days === option.days && 'bg-accent text-accent-foreground'
                )}
              >
                {option.label}
              </button>
            ))}

            {showCustomRange && (
              <>
                <div className="border-t border-border my-2" />
                <button
                  onClick={() => setShowCustom(true)}
                  className={cn(
                    'w-full text-left px-2 py-1.5 text-sm rounded-md hover:bg-accent transition-colors',
                    (value?.from || value?.to) && !value?.days && 'bg-accent text-accent-foreground'
                  )}
                >
                  Custom range...
                </button>
              </>
            )}

            <div className="border-t border-border my-2" />
            <button
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
              className="w-full text-left px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent rounded-md transition-colors"
            >
              All time
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground">Custom date range</p>
              <button
                onClick={() => setShowCustom(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                ← Back
              </button>
            </div>

            <div className="space-y-2">
              <div className="space-y-1">
                <Label htmlFor="date-from" className="text-xs">
                  From
                </Label>
                <Input
                  id="date-from"
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="date-to" className="text-xs">
                  To
                </Label>
                <Input
                  id="date-to"
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-8"
                onClick={handleClearCustom}
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="flex-1 h-8"
                onClick={handleApplyCustom}
                disabled={!customFrom && !customTo}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
